import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { range, smoothstep } from '@/lib/motion/math'
import { gear } from '@/constants/content'

import {
    emptyMetrics,
    enterProgress,
    isNear,
    measureSection,
    pinProgress,
    useEngine
} from '@/lib/motion/section'

import {
    Bracket,
    MaskTitle,
    RevealWords,
    createMaskDriver,
    createStyleCache,
    createWordReveal
} from '@/components/landing/primitives'

const GearSection: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const frameRef = useRef<HTMLDivElement>(null)
    const titleRef = useRef<HTMLHeadingElement>(null)
    const ruleRef = useRef<HTMLDivElement>(null)
    const dividerRef = useRef<HTMLDivElement>(null)
    const copyRef = useRef<HTMLDivElement>(null)
    const labelRef = useRef<HTMLDivElement>(null)
    const notesRef = useRef<HTMLUListElement>(null)

    useEngine(() => {
        const set = createStyleCache()
        const mask = createMaskDriver(titleRef.current)
        const reveal = createWordReveal(copyRef.current)
        const notes = notesRef.current ? Array.from(notesRef.current.children) as Array<HTMLElement> : []

        let metrics = emptyMetrics()

        return {
            measure: () => {
                if (sectionRef.current) metrics = measureSection(sectionRef.current, frameRef.current)
            },
            frame: (state) => {
                if (!isNear(metrics, state.scroll, state.vh)) return

                const enter = enterProgress(metrics, state.scroll, state.vh)
                const p = pinProgress(metrics, state.scroll)

                mask(smoothstep(0.3, 1, enter))
                reveal(range(0.06, 0.72, p))

                set(ruleRef.current, 'transform', `scaleX(${smoothstep(0.35, 1, enter).toFixed(4)})`)
                set(dividerRef.current, 'transform', `scaleY(${smoothstep(0.55, 1, enter).toFixed(4)})`)
                set(labelRef.current, 'opacity', smoothstep(0.6, 1, enter).toFixed(3))

                notes.forEach((note, index) => {
                    const local = range(0.12 + index * 0.1, 0.32 + index * 0.1, p)

                    set(note, 'opacity', local.toFixed(3))
                    set(note, 'transform', `translate3d(0, ${((1 - local) * 12).toFixed(1)}px, 0)`)
                })
            }
        }
    })

    return (
        <section id='gear' ref={sectionRef} aria-labelledby='gear-title' className='relative h-[240vh] md:h-[260vh]'>
            <div ref={frameRef} data-frame='' className='sticky top-0 h-svh overflow-hidden'>
                <div className='absolute inset-x-0 top-[76px] px-4 md:top-[70px] md:px-9'>
                    <h2 id='gear-title' ref={titleRef} className='display-giant text-[clamp(128px,25vw,440px)] text-ink'>
                        <MaskTitle>{gear.title}</MaskTitle>
                    </h2>
                </div>

                <div ref={ruleRef} aria-hidden='true' className='absolute inset-x-3 top-[52%] h-px origin-left bg-line md:inset-x-5 md:top-[57%]' />
                <div ref={dividerRef} aria-hidden='true' className='absolute bottom-0 left-1/2 top-[57%] hidden w-px origin-top bg-line md:block' />

                <div className='absolute inset-x-0 bottom-0 top-[52%] grid grid-rows-[auto_1fr] gap-6 px-5 pb-10 pt-6 md:top-[57%] md:grid-cols-2 md:grid-rows-1 md:gap-0 md:px-10 md:pt-8'>
                    <div className='flex flex-col justify-between gap-6 md:pr-10'>
                        <div ref={labelRef} className='flex items-center gap-4 text-haze'>
                            <Bracket>{gear.label}</Bracket>
                            <span aria-hidden='true' className='h-px w-12 bg-pale' />
                        </div>

                        <ul ref={notesRef} className='hidden flex-col gap-2 md:flex'>
                            {gear.annotations.map(note => (
                                <li key={note} className='flex items-center gap-3 text-haze'>
                                    <span aria-hidden='true' className='size-1.5 rounded-full border border-haze' />
                                    <span className='label-mono'>{note}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className='md:pl-10'>
                        <div ref={copyRef} data-anchor='gear-copy' className='max-w-[640px]'>
                            <p className='text-[clamp(24px,2.5vw,40px)] font-light leading-[1.1] tracking-[-0.035em]'>
                                <RevealWords text={gear.paragraphs[0]} />
                            </p>

                            <p className='mt-5 text-[clamp(17px,1.45vw,23px)] font-light leading-[1.4] tracking-[-0.015em] md:mt-6'>
                                <RevealWords text={gear.paragraphs[1]} />
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default GearSection
